import React from 'react'
import PropTypes from 'prop-types';
import Button from 'common/components/elements/buttons/primary';
import cx from 'classnames'
import './style.scss'
import moment from 'moment';

import Icon from 'common/components/elements/icons';
import NumberFormatter from 'admin/utils/NumberFormatter';
import DateFormatter from 'common/utils/DateFormatter';
import { getCountState } from 'common/utils/utils';
import CheckBox from 'common/components/elements/inputs/checkbox';
import CheckBoxLable from 'common/components/elements/inputs/checkbox-label';
import RadioLabel from 'common/components/elements/inputs/radio-label';
import Radio from 'common/components/elements/inputs/radio';
import { sortableHandle } from 'react-sortable-hoc';
import TruncateText from 'common/components/elements/truncate-text';

const numberFormatter = new NumberFormatter();

// Helper Components
const MediaIconItem = ({
   tooltipContent,
   name,
   iconColor = 'white',
   className = '',
   hasHover,
}) => {
   return (
      <div
         data-tooltip-content={ tooltipContent ? tooltipContent : null }
         data-tooltip-id={ tooltipContent ? 'ams-top-tooltip' : null }
         className='h-5 w-5 flex items-center justify-center'>
         <Icon
            name={ name }
            color={ iconColor }
            size='xl'
            className={ cx({
               [`${ className }`]: true,
               'group-hover:text-secondary': hasHover,
            }) }
         />
      </div>
   )
}

const MediaItemText = ({
   text,
   className = '',
   hasHover,
}) => {
   return (
      <p
         className={ cx({
            [`text-white text-sm font-medium leading-[16px] ${ className }`]: true,
            'group-hover:text-major': hasHover,
         }) }
      >{text}</p>
   )
}

const MediaButtonWrapper = ({
   children,
   hasText = true,
   disabled,
   tooltipContent,
   className = '',
   withoutBackground = false,
   isCenter = true,
   withoutPadding = false,
   hasHover = false,
}) => {
   if(!children) return null;
   return (
      <div
         data-tooltip-content={ tooltipContent ? tooltipContent : null }
         data-tooltip-id={ tooltipContent ? 'ams-top-tooltip' : null }
         className={ cx({
            [`mms-media-button-wrapper flex items-center rounded-lg gap-1 ${ className }`]: true,
            'min-w-[32px] w-8': !hasText && !withoutBackground,
            'p-[6px]': !hasText && !withoutBackground && !withoutPadding,
            'pl-2 pr-3': hasText && !withoutBackground && !withoutPadding,
            'opacity-50': disabled,
            'mms-media-button-wrapper-bg mms-media-button-wrapper-h': !withoutBackground,
            'justify-center': isCenter,
            'hover:bg-panel group': hasHover,
         }) }
      >
         {children}
      </div>
   )
}

const DragHandle = sortableHandle(({ text, tooltipContent, hasHover }) => (
   <div className='flex gap-1 items-center drag-cursor' >
      <MediaIconItem
         tooltipContent={ tooltipContent }
         name='drag'
         hasHover={ hasHover }
      />

      {
         text && (
            <MediaItemText
               text={ text }
               hasHover={ hasHover }
            />
         )
      }
   </div>
));

const MediaItem = ({
   iconName,
   text,
}) => {
   return (
      <div
         className='flex items-center justify-center gap-1 h-full'
      >
         <MediaIconItem
            name={ iconName }
         />
         <MediaItemText
            text={ text }
         />
      </div>
   )
}

// Export Modules

const MediaDragHandle = ({
   text,
   tooltipContent,
}) => {

   return (
      <MediaButtonWrapper
         hasHover
      >
         <DragHandle
            text={ text }
            tooltipContent={ tooltipContent }
            hasHover
         />
      </MediaButtonWrapper>
   )
}

const MediaButton = ({
   iconName,
   onClick = () => {},
   dataToolTipContent,
   text,
   hasWrapper = true,
   withoutBackground = false,
   disabled,
   className = '',
   fontIconColor = 'white',
   fontIconSize = 'lg',
   isInsideModal = false,
   isPreventDefault = true,
}) => {
   const buttonComponent = (
      <div
         role='presentation'
         data-tooltip-content={ dataToolTipContent }
         data-tooltip-id={ dataToolTipContent ? 'ams-top-tooltip' : null }
         className={ cx({
            'flex items-center justify-center gap-1 group': true,
            'w-6': !text && withoutBackground,
            'opacity-50': disabled,
            'cursor-pointer ': !disabled,
            'h-6': withoutBackground,
            'h-full w-full': !withoutBackground,
            'p-[6px]': !text && !withoutBackground,
            'pl-2 pr-3': text && !withoutBackground,
         }) }
         onClick={ (e) => {
            if(isPreventDefault){
               e.stopPropagation();
               e.preventDefault();
            }
            onClick(e)
         } }
         disabled={ disabled }
      >

         <MediaIconItem
            name={ iconName }
            iconColor={ fontIconColor }
            size={ fontIconSize }
            hasHover
         />
         {
            text && (
               <MediaItemText
                  text={ text }
                  hasHover
               />
            )
         }
      </div>
   )
   if(!hasWrapper){
      return (
         <>{buttonComponent}</>
      )
   }
   return (
      <MediaButtonWrapper
         className={ className }
         disabled={ disabled }
         hasText={ Boolean(text) }
         withoutBackground={ withoutBackground }
         withoutPadding
         hasHover
      >
         {buttonComponent}
      </MediaButtonWrapper>
   )
}

const MediaCheckBox = ({
   label,
   checked,
   onChange = () => {},
   disabled,
   name,
   withoutBackground,
}) => {
   return (
      <MediaButtonWrapper
         hasText={ Boolean(label) }
         withoutBackground={ withoutBackground }
         hasHover
      >
         {
            !label && (
               <CheckBox
                  name={ name }
                  isChecked={ checked }
                  onChange={ onChange }
                  disabled={ disabled }
                  checkboxSize='w-[18px] h-[18px]'
                  classNames={ `!text-major-dark  !z-40 ${ disabled ? '!cursor-default' : '' }` }
               />

            )
         }
         {
            label && (
               <CheckBoxLable
                  label={ label }
                  name={ name }
                  checked={ checked }
                  onChange={ onChange }
                  labelTextClassName='text-major-dark font-medium group-hover:text-major'
                  checkboxSize='6'
                  padding='0'
                  margin='0'
                  checkboxSize2='w-5 h-5'
                  labelClassName='flex items-center'
               />
            )
         }
      </MediaButtonWrapper>
   )
}
const MediaRadio = ({
   label,
   checked,
   onChange = () => {},
   disabled,
   id,
   withoutBackground,
}) => {
   return (
      <MediaButtonWrapper
         hasText={ Boolean(label) }
         withoutBackground={ withoutBackground }
         hasHover
      >
         {
            !label && (
               <Radio
                  id={ id }
                  checked={ checked }
                  onChange={ onChange }
                  disabled={ disabled }
                  className={ `!text-major-dark !z-40 ${ disabled ? '!cursor-default' : '' }` }
               />

            )
         }
         {
            label && (
               <RadioLabel
                  label={ label }
                  id={ id }
                  checked={ checked }
                  onChange={ onChange }
                  labelClassName='flex h-full items-center justify-center group-hover:text-major'
                  className='!p-0 flex-row-reverse'
                  radioParentClassName='ml-0 w-[18px] h-[18px] flex items-center justify-center'
                  disabled={ disabled }
               />
            )
         }
      </MediaButtonWrapper>
   )
}

const MediaChip = ({
   countOrDuration,
   type,
   name = 'photo',
   photoIcon = 'several-images',
}) => {
   if(!countOrDuration){
      return null
   }
   let newcount = countOrDuration;
   if('video' === type || type.includes('video')){
      newcount = DateFormatter.getSecondsConversion(countOrDuration);
   } else {
      const numberFormatter = new NumberFormatter();
      newcount = getCountState(numberFormatter.withCommas(countOrDuration), name, countOrDuration)
   }
   return (
      <MediaButtonWrapper>
         <MediaIconItem
            name={ 'photoset' === type ? photoIcon : 'play-default' }
         />
         <MediaItemText
            text={ newcount }
         />

      </MediaButtonWrapper>
   )
}

const MediaLableItem = ({
   iconName,
   text,
   tooltipContent,
   iconColor,
   withoutBackground,
}) => {
   return (
      <MediaButtonWrapper
         hasText={ Boolean(text)  }
         tooltipContent={ tooltipContent }
         withoutBackground={ withoutBackground }
      >

         {
            iconName && (
               <MediaIconItem
                  name={ iconName }
                  iconColor={ iconColor }
               />

            )
         }
         {
            text && (
               <MediaItemText
                  text={ text }
               />

            )
         }
      </MediaButtonWrapper>
   )
}

const MediaDashboardBottonOptions = ({
   viewsCount,
   likesCount,
   commentsCount,
   disabled,
   totalTip,
   sentTip,
   canShowViewsCount = false,
   canShowLikesCount = true,
   canShowCommentsCount = true,
   canShowTotalTip = true,
   onClickComment,
   isInsideModal,
}) => {
   return (
      <div
         className='flex gap-2 mb-2 ml-2'
      >
         {
            canShowViewsCount && (
               <MediaButtonWrapper
                  tooltipContent='Views'
               >
                  <MediaItem
                     iconName='views'
                     text={ numberFormatter.withCommas(viewsCount) }
                  />
               </MediaButtonWrapper>
            )
         }
         {
            canShowLikesCount && (
               <MediaButtonWrapper
                  tooltipContent='Likes'
               >
                  <MediaItem
                     iconName='heart-filled'
                     text={ numberFormatter.withCommas(likesCount) }
                  />
               </MediaButtonWrapper>

            )
         }
         {
            canShowCommentsCount && (
               <MediaButton
                  iconName='comment'
                  text={  numberFormatter.withCommas(commentsCount) }
                  disabled={ disabled }
                  onClick={ (e) => {
                     if(onClickComment){
                        onClickComment()
                     }
                  } }
                  dataToolTipContent='Comments'
                  isInsideModal={ isInsideModal }
                  isPreventDefault={ Boolean(onClickComment) }
               />
            )
         }
         {
            canShowTotalTip && (
               <MediaButton
                  iconName='tip'
                  text={  `${ numberFormatter.withCommas(totalTip) } USD` }
                  disabled={ disabled }
                  onClick={ (e) => {
                     e.preventDefault()
                     e.stopPropagation()
                     sentTip()
                  } }
                  dataToolTipContent='Tips on content'
                  isInsideModal={ isInsideModal }
               />
            )
         }
      </div>
   )
}

const MediaInfoChip = ({ text }) => {
   return (
      <div className='rounded-lg h-8 px-3  bg-pending  inline-flex items-center'>
         <span className='font-medium text-sm text-major leading-[16px]'>{text}</span>
      </div>
   )
}

const MediaMemberBottonOptions = ({
   isLiked,
   id,
   onClickLike = () => {},
   canLike,
   canShare,
   onShare = () => {},
   canSendTip,
   onClickTip = () => {},
   collectionType,
   isFavorite,
   onChangeFavoriteItem = () => {},
   canChangeFavorite,
}) => {

   return (
      <div
         role='presentation'
         onClick={ e => e.preventDefault() }
         className='flex gap-2 justify-end ml-auto h-full'>
         {
            id && (
               <div id={ `content_card_mute_unmute_${ id }` }></div>
            )
         }
         {
            canLike && (
               <MediaButton
                  iconName={ !!isLiked ? 'heart-filled' : 'heart-outline' }
                  onClick={ onClickLike }
                  dataToolTipContent='Like'
               />
            )
         }
         {
            canSendTip &&
               <MediaButton
                  iconName='tip'
                  onClick={ onClickTip }
                  dataToolTipContent={ `Tip for this ${ collectionType }` }
               />
         }
         {
            canChangeFavorite &&
               <MediaButton
                  iconName={ `star${ isFavorite ? '' : '-outline' }` }
                  onClick={ onChangeFavoriteItem }
                  dataToolTipContent={ isFavorite ? 'Remove from favorites' : 'Add to favorites' }
               />
         }
         {
            canShare &&
               <MediaButton
                  iconName='share'
                  onClick={ onShare }
                  dataToolTipContent='Share'
               />
         }

      </div>
   )
}

const MediaCardBottomView = ({
   isPinned,
   title,
   disabled,
   viewsCount,
   points,
   onClickOptions,
   optionButtonClass = '',
   showTimeFromNow,
   statusName,
   date,
   showViews = true,
   drmProtection,
   showDateTooltip = true,
   showChooseInput,
   isMultipleSelect,
   checked,
}) => {
   const isShowViews = Boolean(showViews && Number.isInteger(viewsCount));

   return (
      <div className='flex justify-between w-full bg-panel dark:bg-panel-dark gap-2 p-3 mms-media-card-bottom-content'>
         <div className='flex gap-2'>
            {
               isPinned && (
                  <div
                     data-tooltip-id='ams-top-tooltip'
                     data-tooltip-content='Pinned'
                     className='flex w-5 h-5'>
                     <Icon
                        name='pin-35'
                        color='warning'
                        darkColor='warning-dark'
                        size='lg'
                     />
                  </div>
               )
            }
            <div className='flex flex-col gap-1'>
               <div
                  className='w-full flex items-center gap-2'
               >
                  {
                     drmProtection && (
                        <div
                           data-tooltip-id='ams-top-tooltip'
                           data-tooltip-content='Enhanced Video Security (DRM)'
                           className='flex w-5 h-5'>

                           <Icon
                              name='security'
                              color='action'
                              darkColor='action-dark'
                              size='lg'
                           />
                        </div>
                     )
                  }
                  <TruncateText
                     className='pr-1'
                     text={ title }
                     textClass={ cx({
                        'font-semibold text-base leading-[20px]': true,
                        'text-disabled dark:text-disabled-dark': disabled,
                        'text-major dark:text-major-dark': !disabled,
                     }) }
                     textSize='16px'
                     fontWeight='600'
                  />
               </div>
               {
                  (isShowViews || showTimeFromNow) && (
                     <div
                        className={ cx({
                           'flex flex-wrap items-center h-4': true,
                           'text-disabled dark:text-disabled-dark': disabled,
                           'text-placeholder dark:text-placeholder-dark': !disabled,
                        }) }
                     >
                        {
                           isShowViews && (
                              <div
                                 className='h-full items-start flex'
                                 data-tooltip-content={ `${ viewsCount } views` }
                                 data-tooltip-id={ viewsCount > 999 ? 'ams-top-tooltip' : null }
                              >
                                 <span className='text-xs leading-[16px]'>
                                    {getCountState(viewsCount, 'view', null, true)}
                                    {
                                       showTimeFromNow && (
                                          <>&nbsp;•&nbsp;</>
                                       )
                                    }
                                 </span>
                              </div>
                           )
                        }
                        {
                           showTimeFromNow && (
                              <div
                                 className='h-full items-start flex'
                                 data-tooltip-content={ `${ DateFormatter.convertToUserTz(date, 'MMM DD, YYYY hh:mm A') } ${ (DateFormatter.getZoonFullName()) } ` }
                                 data-tooltip-id={ showDateTooltip ? 'ams-top-tooltip' : null }
                              >
                                 <span className='text-xs leading-[16px]'>
                                    { statusName } { moment(DateFormatter.convertToUserTz(date)).fromNow() }
                                 </span>
                              </div>
                           )
                        }

                     </div>
                  )
               }
            </div>
         </div>
         {
            !showChooseInput && points && (
               <div
                  className={ `flex justify-center items-center ${ optionButtonClass } cursor-pointer h-6 w-6` }
               >
                  <Button
                     onClick={ (e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        onClickOptions(e) }
                     }
                     backgroundColor='transparent'
                     fontIconColor={ disabled ? 'disabled' : 'secondary' }
                     darkIconColor='secondary-dark'
                     iconName='options-vertical'
                     fontIconSize='doubleLarge'
                     padding='0'
                  />
               </div>
            )
         }
         {
            showChooseInput && (
               <div className='mms-media-button-wrapper'>
                  {
                     isMultipleSelect && (

                        <CheckBox
                           isChecked={ checked }
                           onChange={ () => {} }
                           disabled={ disabled }
                           checkboxSize='w-[18px] h-[18px]'
                           classNames={ `!text-major-dark !z-40 ${ disabled ? '!cursor-default' : '' }` }
                        />
                     )
                  }
                  {
                     !isMultipleSelect && (
                        <Radio
                           checked={ checked }
                           onChange={ () => {} }
                           disabled={ disabled }
                           className={ `!text-major-dark !z-40 ${ disabled ? '!cursor-default' : '' }` }
                        />
                     )
                  }
               </div>

            )
         }
      </div>
   )
}

const MediaUnavailableInCurrentMembership = ({
   accessType,
   upgradeMembership,
   onClick,
   pageType,
}) => {

   const SUFFIX = 'photoset' === pageType ? 'view' : 'watch';

   if('active_member_not_available' !== accessType){
      const getUpgradeButtonText = () => {
         return  `Upgrade to ${ upgradeMembership.name } to ${ SUFFIX }`
      }
      return (
         <Button
            text={ getUpgradeButtonText() }
            iconPosition='left'
            iconName='membership-upgrade'
            classNames={ cx({
               'lg:!w-full max-sm:!w-full !w-fit h-11 px-4 py-2 sm:whitespace-nowrap': true,
            }) }
            borderRadius='large'
            lineHeight='24px'
            textSize='base'
            fontIconSize='doubleLarge'
            textMarginX='0'
            textClassName='ml-2'
            primaryColor
            onClick={ onClick }
         />
      )
   }

   const getStateSuffix = () => {
      let suffix = SUFFIX;
      if(upgradeMembership?.unlock_price){
         suffix = 'unlock'
      }
      return suffix
   }
   const suffix = getStateSuffix();

   return (
      <div className='flex flex-col gap-3 w-full text-center items-center'>
         {
            'active_member_not_available' === accessType && (
               <>
                  <div className='flex rounded-full h-6 items-center px-2 gap-1 bg-error-12 dark:bg-error-dark-12 mx-auto'>
                     <Icon
                        size='base'
                        name='membership'
                        color='error'
                        darkColor='error-dark'
                     />
                     <span className='text-sm text-error dark:text-error-dark leading-[18px]'>
                        Unavailable
                     </span>
                  </div>

                  {
                     !upgradeMembership && (
                        <span className='text-sm  leading-[18px] text-secondary dark:text-secondary-dark'>
                           This content is not available with your current membership
                        </span>
                     )
                  }
               </>
            )
         }
         {
            upgradeMembership && (
               <>
                  <span className='text-sm  leading-[18px] text-secondary dark:text-secondary-dark'>
                     This content is not available with your current membership.
                     <br />
                     Upgrade to <span className='font-bold'>{upgradeMembership.name}</span> membership to {suffix}.
                  </span>
                  <Button
                     classNames={ cx({
                        'lg:!w-full !w-fit max-sm:!w-full px-4 py-2 border sm:whitespace-nowrap skin-primary-border h-11 bg-panel dark:bg-panel-dark': true,
                     }) }
                     text={ `Upgrade membership to ${ suffix }` }
                     iconPosition='left'
                     iconName='membership-upgrade'
                     backgroundColor='transparent'
                     primaryColor
                     borderRadius='large'
                     fontIconSize='doubleLarge'
                     onClick={ onClick }
                     textSize='base'
                     textMarginX='0'
                     textClassName='ml-2'
                  />
               </>
            )
         }
      </div>
   )
}

// components props


MediaIconItem.propTypes = {
   name: PropTypes.string,
   tooltipContent: PropTypes.string,
   iconColor: PropTypes.string,
   className: PropTypes.string,
   hasHover: PropTypes.bool,
};
MediaItemText.propTypes = {
   text: PropTypes.string,
   className: PropTypes.string,
   hasHover: PropTypes.bool,

};
MediaItem.propTypes = {
   iconName: PropTypes.string,
   text: PropTypes.string,
};
MediaDragHandle.propTypes = {
   text: PropTypes.string,
   tooltipContent: PropTypes.string,
};
MediaButtonWrapper.propTypes = {
   children: PropTypes.any,
   hasText: PropTypes.bool,
   hasHover: PropTypes.bool,
   disabled: PropTypes.bool,
   tooltipContent: PropTypes.string,
   className: PropTypes.string,
   withoutBackground: PropTypes.bool,
   isCenter: PropTypes.bool,
   withoutPadding: PropTypes.bool,
};
// modules props

MediaMemberBottonOptions.propTypes = {
   id: PropTypes.any,
   collectionType: PropTypes.string,
   onShare: PropTypes.func,
   onClickLike: PropTypes.func,
   onChangeFavoriteItem: PropTypes.func,
   onClickTip: PropTypes.func,
   canChangeFavorite: PropTypes.bool,
   isLiked: PropTypes.bool,
   canLike: PropTypes.bool,
   canShare: PropTypes.bool,
   canSendTip: PropTypes.bool,
   isFavorite: PropTypes.bool,
};
MediaDashboardBottonOptions.propTypes = {
   viewsCount: PropTypes.any,
   likesCount: PropTypes.any,
   commentsCount: PropTypes.any,
   totalTip: PropTypes.any,
   sentTip: PropTypes.func,
   disabled: PropTypes.bool,
   canShowViewsCount: PropTypes.bool,
   canShowLikesCount: PropTypes.bool,
   canShowCommentsCount: PropTypes.bool,
   canShowTotalTip: PropTypes.bool,
   onClickComment: PropTypes.func,
   isInsideModal: PropTypes.bool,
};
MediaLableItem.propTypes = {
   iconName: PropTypes.string,
   text: PropTypes.string,
   tooltipContent: PropTypes.string,
   iconColor: PropTypes.string,
   withoutBackground: PropTypes.bool,
};
MediaChip.propTypes = {
   photoIcon: PropTypes.string,
   name: PropTypes.string,
   type: PropTypes.string,
   countOrDuration: PropTypes.any,
};
MediaButton.propTypes = {
   iconName: PropTypes.string,
   text: PropTypes.string,
   dataToolTipContent: PropTypes.string,
   onClick: PropTypes.func,
   disabled: PropTypes.bool,
   hasWrapper: PropTypes.bool,
   className: PropTypes.string,
   withoutBackground: PropTypes.bool,
   fontIconColor: PropTypes.string,
   fontIconSize: PropTypes.string,
   isInsideModal: PropTypes.bool,
   isPreventDefault: PropTypes.bool,
};
MediaRadio.propTypes = {
   label: PropTypes.string,
   checked: PropTypes.bool,
   withoutBackground: PropTypes.bool,
   disabled: PropTypes.bool,
   onChange: PropTypes.func,
   id: PropTypes.string,
};
MediaCheckBox.propTypes = {
   label: PropTypes.string,
   checked: PropTypes.bool,
   disabled: PropTypes.bool,
   withoutBackground: PropTypes.bool,
   onChange: PropTypes.func,
   name: PropTypes.string,
};
MediaInfoChip.propTypes = {
   text: PropTypes.string,
};
MediaCardBottomView.propTypes = {
   isPinned: PropTypes.bool,
   checked: PropTypes.bool,
   isMultipleSelect: PropTypes.bool,
   showChooseInput: PropTypes.bool,
   drmProtection: PropTypes.bool,
   showDateTooltip: PropTypes.bool,
   disabled: PropTypes.bool,
   showViews: PropTypes.bool,
   showTimeFromNow: PropTypes.bool,
   title: PropTypes.string,
   statusName: PropTypes.string,
   date: PropTypes.string,
   optionButtonClass: PropTypes.string,
   viewsCount: PropTypes.any,
   points: PropTypes.any,
   onClickOptions: PropTypes.func,
};
MediaUnavailableInCurrentMembership.propTypes = {
   accessType: PropTypes.string,
   pageType: PropTypes.string,
   upgradeMembership: PropTypes.object,
   onClick: PropTypes.func,
};
export {
   MediaButton,
   MediaChip,
   MediaDashboardBottonOptions,
   MediaCheckBox,
   MediaRadio,
   MediaDragHandle,
   MediaInfoChip,
   MediaLableItem,
   MediaMemberBottonOptions,
   MediaButtonWrapper,
   MediaCardBottomView,
   MediaUnavailableInCurrentMembership,
}
